import { useCallback, useEffect, useRef } from 'react';

/**
 * 팝업 유틸리티 함수
 */

/**
 * 버튼 클래스명 변환 함수 (기존 커스텀 클래스 → Bootstrap 표준 클래스)
 * @param {string} colorClass - 버튼 색상 클래스
 * @returns {string} 변환된 Bootstrap 클래스
 */
export const getButtonClass = (colorClass) => {
  const classMap = {
    'btn-black': 'btn-secondary',
    'btn-red': 'btn-primary',
    'btn-primary': 'btn-primary',
    'btn-secondary': 'btn-secondary',
    'btn-success': 'btn-success',
    'btn-danger': 'btn-danger',
    'btn-warning': 'btn-warning',
    'btn-info': 'btn-info',
    'btn-light': 'btn-light',
    'btn-dark': 'btn-dark',
    'btn-outline-primary': 'btn-outline-primary',
    'btn-outline-secondary': 'btn-outline-secondary',
    'btn-outline-success': 'btn-outline-success',
    'btn-outline-danger': 'btn-outline-danger',
    'btn-outline-warning': 'btn-outline-warning',
    'btn-outline-info': 'btn-outline-info',
    'btn-outline-light': 'btn-outline-light',
    'btn-outline-dark': 'btn-outline-dark',
  };

  if (colorClass && colorClass.startsWith('btn-')) {
    return classMap[colorClass] || colorClass;
  }
  return colorClass || 'btn-secondary';
};

/**
 * alert/confirm 등에서 Enter로 실행할 기본(주) 버튼 — 우측 확인 우선
 * @param {object} [config]
 * @returns {'left'|'right'}
 */
export const resolvePopupPrimaryButtonSide = (config = {}) => {
  if (config.primaryButton === 'left' || config.primaryButton === 'right') {
    return config.primaryButton;
  }
  const left = config.leftButton;
  const right = config.rightButton;
  if (right && right.submit !== false) return 'right';
  if (left && left.submit === true) return 'left';
  if (right) return 'right';
  if (left) return 'left';
  return 'right';
};

/**
 * 텍스트형 alert/confirm — 주 버튼 자동 포커스 + Enter로 주 액션 실행
 * config.autoFocusPrimaryButton === false 이면 비활성
 */
export const usePopupPrimaryButton = (config, onPrimaryAction) => {
  const primaryButtonRef = useRef(null);

  const focusPrimaryButton = useCallback(() => {
    primaryButtonRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (config?.autoFocusPrimaryButton === false) return undefined;
    const timer = window.setTimeout(focusPrimaryButton, 0);
    return () => window.clearTimeout(timer);
  }, [config, focusPrimaryButton]);

  useEffect(() => {
    if (config?.autoFocusPrimaryButton === false) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.isComposing || e.keyCode === 229) return;

      const target = e.target;
      if (
        target instanceof HTMLElement
        && target.closest('textarea, input, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (primaryButtonRef.current && target === primaryButtonRef.current) {
        return;
      }

      e.preventDefault();
      onPrimaryAction();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [config, onPrimaryAction]);

  return primaryButtonRef;
};

